/*
 * Leaf metadata for the HMI widgets.
 *
 * A widget used to know only what the project says. What the leaf already
 * knows (unit, decimals, enum texts, limits, writability) the author would
 * otherwise enter a second time, and every copy can go stale. So it is read
 * at runtime and not copied into the project.
 *
 * Rule for all widgets: **what the widget says wins.** The metadata only
 * fills gaps.
 *
 * Display text of values lives in `format/leafValue.ts`, shared with the
 * datapoint page and the dashboard.
 *
 * Type-only imports, so the Node test run can load the file without a
 * bundler. The Preact context is in `LeafMetaContext.tsx`.
 */

import type { LeafDto, NodeDto, PlantSchemaResponse } from '../api/types';
import type { LeafValueFormat } from '../format/leafValue';
import type { GaugeZone } from './widgets';
import type { HmiLeafInfo } from './staleness';

/**
 * Everything a widget can know about its leaf without it being in the
 * project. Extends {@link HmiLeafInfo} so the same table also serves the live
 * source's staleness threshold and there is only ONE index.
 */
export interface HmiLeafMeta extends HmiLeafInfo, LeafValueFormat {
  unit?: string | null;
  minValue?: number | null;
  maxValue?: number | null;
  warnLow?: number | null;
  warnHigh?: number | null;
  alarmLow?: number | null;
  alarmHigh?: number | null;
  writable?: boolean;
}

/** Index lower-case leaf GUID -> metadata. */
export function buildLeafMetaIndex(schema: PlantSchemaResponse | null): Map<string, HmiLeafMeta> {
  const index = new Map<string, HmiLeafMeta>();
  if (!schema) return index;
  const walk = (node: NodeDto): void => {
    for (const leaf of node.leaves) index.set(leaf.id.toLowerCase(), metaFromLeaf(leaf));
    for (const child of node.children) walk(child);
  };
  for (const device of schema.devices) {
    for (const node of device.nodes) walk(node);
  }
  return index;
}

export function metaFromLeaf(leaf: LeafDto): HmiLeafMeta {
  return {
    label: leaf.displayName && leaf.displayName.length > 0 ? leaf.displayName : leaf.symbol,
    pollIntervalSeconds: leaf.pollIntervalSeconds,
    heartbeatSeconds: leaf.heartbeatSeconds,
    valueType: leaf.valueType,
    decimals: leaf.decimals,
    enumEntries: leaf.enumEntries,
    flagBits: leaf.flagBits,
    unit: leaf.unit,
    minValue: leaf.minValue,
    maxValue: leaf.maxValue,
    warnLow: leaf.warnLow,
    warnHigh: leaf.warnHigh,
    alarmLow: leaf.alarmLow,
    alarmHigh: leaf.alarmHigh,
    writable: leaf.writability !== 'ReadOnly',
  };
}

export type LimitTone = 'warn' | 'alarm';

/**
 * Gauge zones from the leaf's limits:
 *
 *   min ─ alarm ─ alarmLow ─ warn ─ warnLow  [normal]  warnHigh ─ warn ─ alarmHigh ─ alarm ─ max
 *
 * Missing limits drop out; everything is clamped to the widget's scale.
 * Without limits `[]`, and the gauge stays single-coloured.
 */
export function zonesFromLeaf(meta: HmiLeafMeta | undefined, min: number, max: number): GaugeZone[] {
  if (!meta) return [];
  const { warnLow, warnHigh, alarmLow, alarmHigh } = meta;
  const zones: GaugeZone[] = [];
  const push = (from: number, to: number, tone: LimitTone) => {
    const a = Math.min(max, Math.max(min, from));
    const b = Math.min(max, Math.max(min, to));
    if (b > a) zones.push({ from: a, to: b, tone });
  };
  if (alarmLow != null) push(min, alarmLow, 'alarm');
  if (warnLow != null) push(alarmLow ?? min, warnLow, 'warn');
  if (warnHigh != null) push(warnHigh, alarmHigh ?? max, 'warn');
  if (alarmHigh != null) push(alarmHigh, max, 'alarm');
  return zones;
}

/** Tone of a reading against the limits, for the colour of the number. */
export function toneFor(meta: HmiLeafMeta | undefined, value: number | null): LimitTone | null {
  if (!meta || value === null) return null;
  if ((meta.alarmLow != null && value < meta.alarmLow) || (meta.alarmHigh != null && value > meta.alarmHigh)) return 'alarm';
  if ((meta.warnLow != null && value < meta.warnLow) || (meta.warnHigh != null && value > meta.warnHigh)) return 'warn';
  return null;
}
